import { useState, useEffect } from 'react'
import { useSearchParams, useNavigate, Link } from 'react-router-dom'
import { useTenant } from '../contexts/TenantContext'
import { Button } from '../components/Button'
import { Input } from '../components/Input'
import { Card } from '../components/Card'
import { passwordResetApi } from '../services/api'
import logo from '../assets/El CapIT_logo_top_left.png'

export default function ResetPassword() {
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token') || ''

  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)

  const { tenant } = useTenant()
  const navigate = useNavigate()
  const gymLogo = tenant?.branding?.logoUrl
  const gymName = tenant?.branding?.gymName

  // Redirect to forgot-password if no token in URL
  useEffect(() => {
    if (!token) {
      navigate('/customer/forgot-password', { replace: true })
    }
  }, [token, navigate])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError('')

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match')
      return
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters')
      return
    }

    setLoading(true)
    try {
      await passwordResetApi.confirmForgotPassword(token, newPassword)
      setSuccess(true)
    } catch (err: any) {
      const msg = err?.response?.data?.error?.message || err?.message || ''
      if (msg.toLowerCase().includes('expired') || msg.toLowerCase().includes('invalid')) {
        setError('This reset link has expired or already been used. Please request a new one.')
      } else if (msg.toLowerCase().includes('password')) {
        setError(msg)
      } else {
        setError('Something went wrong. Please try again or request a new reset link.')
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-neutral-50 flex items-center justify-center px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          {gymLogo ? (
            <img src={gymLogo} alt={gymName} className="h-24 mx-auto mb-6 object-contain" />
          ) : (
            <img src={logo} alt="El CapIT" className="h-48 mx-auto mb-6" />
          )}
        </div>

        <Card padding="lg">
          {success ? (
            <div className="text-center space-y-4">
              <div className="flex items-center justify-center w-16 h-16 mx-auto rounded-full bg-success-light/20">
                <svg className="w-8 h-8 text-success-DEFAULT" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-neutral-900">Password updated!</h2>
              <p className="text-neutral-600">Your password has been reset successfully. You can now sign in with your new password.</p>
              <Button
                variant="primary"
                size="lg"
                fullWidth
                onClick={() => navigate('/customer/login')}
              >
                Sign in
              </Button>
            </div>
          ) : (
            <>
              <h2 className="text-2xl font-bold text-neutral-900 mb-2">Set a new password</h2>
              <p className="text-neutral-600 mb-6">
                Choose a strong password for your account.
              </p>

              {error && (
                <div className="mb-6 p-4 bg-error-light/20 border border-error-DEFAULT rounded-lg">
                  <p className="text-sm text-error-dark">{error}</p>
                  {error.includes('expired') && (
                    <Link
                      to="/customer/forgot-password"
                      className="mt-2 inline-block text-sm font-medium text-error-dark underline"
                    >
                      Request a new link
                    </Link>
                  )}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <Input
                  type="password"
                  label="New password"
                  value={newPassword}
                  onChange={setNewPassword}
                  placeholder="••••••••"
                  required
                  disabled={loading}
                />

                <Input
                  type="password"
                  label="Confirm new password"
                  value={confirmPassword}
                  onChange={setConfirmPassword}
                  placeholder="••••••••"
                  required
                  disabled={loading}
                />

                <p className="text-xs text-neutral-500">
                  Must be at least 8 characters with uppercase, lowercase, and a number.
                </p>

                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  fullWidth
                  disabled={loading || !newPassword || !confirmPassword}
                  loading={loading}
                >
                  {loading ? 'Updating...' : 'Set new password'}
                </Button>
              </form>
            </>
          )}
        </Card>

        {gymName && (
          <p className="text-center text-sm text-neutral-500">{gymName} · Powered by El CapIT<br />A BuilderBias product</p>
        )}
      </div>
    </div>
  )
}
